import { useEffect, useState } from 'react';
import { Loader2, Copy, Check, Unlink, MessageCircle } from 'lucide-react';

export interface ChatBindingStatus {
  enabled: boolean;
  linked: boolean;
  chat_id: string | null;
  bot_username: string;
  pending_code: string | null;
  pending_expires_at?: string | null;
}

export interface ChatBindingApi {
  status: () => Promise<ChatBindingStatus>;
  link: () => Promise<void>;
  unlink: () => Promise<void>;
}

interface Props {
  title: string;
  channel: 'max' | 'tg';
  icon: React.ReactNode;
  /** CSS-цвет для иконки + акцентов (border, button). */
  accentClass: string;        // например 'text-orange-400' / 'text-sky-400'
  buttonClass: string;        // 'bg-orange-600 hover:bg-orange-500' и т.п.
  startCommandUrlPrefix: string; // 'https://max.ru/' | 'https://t.me/'
  api: ChatBindingApi;
  helpLink?: string;          // ссылка на бота в нативном клиенте
}

/** Универсальная карточка привязки бота к групповому чату организации.
 * Используется одинаково для MAX и Telegram — отличия только в иконке/цвете. */
export default function ChatBindingCard({
  title, channel, icon, accentClass, buttonClass, startCommandUrlPrefix, api,
}: Props) {
  const [status, setStatus] = useState<ChatBindingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setErr(null);
    try { setStatus(await api.status()); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
    finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); }, []);
  useEffect(() => {
    if (!status?.pending_code || status?.linked) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [status?.pending_code, status?.linked]);

  const requestCode = async () => {
    setBusy(true); setErr(null);
    try { await api.link(); await load(); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
    finally { setBusy(false); }
  };
  const doUnlink = async () => {
    if (!window.confirm(`Отвязать ${title} от этой организации?`)) return;
    setBusy(true); setErr(null);
    try { await api.unlink(); await load(); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
    finally { setBusy(false); }
  };
  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  // Унифицированный статус-бейдж
  const statusBadge = (() => {
    if (!status) return null;
    if (status.linked) return (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/30 border border-emerald-800 text-emerald-300">привязан</span>
    );
    if (status.pending_code) return (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/30 border border-yellow-800 text-yellow-300">ждём /start</span>
    );
    if (!status.enabled) return (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-500">бот не настроен</span>
    );
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400">не привязан</span>
    );
  })();

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
          <span className={accentClass}>{icon}</span> {title}
        </div>
        {statusBadge}
      </div>

      {loading ? (
        <Loader2 size={14} className="animate-spin text-blue-400" />
      ) : err && !status ? (
        <p className="text-xs text-red-400">{err}</p>
      ) : !status?.enabled ? (
        <p className="text-xs text-slate-500">
          Бот не сконфигурирован. Задайте токен и username в
          <b> Настройки → Интеграции</b>.
        </p>
      ) : status.linked ? (
        <div className="space-y-2">
          <div className="text-xs text-slate-300">
            <MessageCircle size={11} className="inline -mt-0.5 mr-1" />
            Чат <span className="font-mono text-slate-400">{status.chat_id}</span>
          </div>
          <button onClick={doUnlink} disabled={busy}
            className="w-full text-xs inline-flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-red-900/40 hover:text-red-300 text-slate-300 px-3 py-1.5 rounded transition">
            <Unlink size={12} /> Отвязать
          </button>
        </div>
      ) : status.pending_code ? (
        <div className="space-y-2">
          <p className="text-[11px] text-slate-400">
            Добавьте бота <a href={`${startCommandUrlPrefix}${status.bot_username}`}
              target="_blank" rel="noreferrer"
              className={`hover:underline ${accentClass}`}>@{status.bot_username}</a> в групповой чат
            клиента (для TG — выключите Privacy у бота) и попросите кого-то в группе прислать:
          </p>
          <div className="flex items-center gap-2 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 font-mono text-blue-300 text-sm">
            <span className="flex-1 truncate">/start {status.pending_code}</span>
            <button onClick={() => copy(`/start ${status.pending_code}`)}
              className="text-slate-500 hover:text-white" title="Скопировать">
              {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
            </button>
          </div>
          <p className="text-[10px] text-slate-600">
            Код активен 30 мин. Карточка обновится автоматически.
          </p>
          <button onClick={requestCode} disabled={busy}
            className="text-[11px] text-slate-500 hover:text-white underline">
            Запросить новый код
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-slate-400">
            Не привязан. Получи код и добавь
            <a href={`${startCommandUrlPrefix}${status.bot_username}`}
               target="_blank" rel="noreferrer"
               className={`mx-1 hover:underline ${accentClass}`}>@{status.bot_username}</a>
            в групповой чат клиента.
          </p>
          <button onClick={requestCode} disabled={busy}
            className={`w-full text-xs ${buttonClass} disabled:bg-slate-700 text-white font-bold px-3 py-1.5 rounded inline-flex items-center justify-center gap-1.5`}>
            {busy && <Loader2 size={12} className="animate-spin" />}
            Получить код привязки
          </button>
        </div>
      )}
      {err && status && (
        <div className="mt-2 text-[11px] text-red-400">{err}</div>
      )}
    </div>
  );
}
